import { Component, Inject, type OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { BaseMatcherEditorComponent } from '@cdw-ae/filter-bar-core';
import { CdwFilteredTreePickerModule } from '@cdw-ae/common-ui-components-filtered-tree-picker';
import { CommonUiComponentsSpinnerComponent } from '@cdw-ae/common-ui-components-spinner';
import type { TreeNode } from '@cdw-ae/common-ui-components-tree';
import { OuTreePickerMatcher } from '@cdw-ae/filter-bar-matcher-configs';
import type { CollapsedOuSelection } from '@cdw-ae/filter-bar-interfaces';
import { map } from 'rxjs/operators';
import type { IOrgUnitsDataService } from './org-units-data-service.interface';
import { OU_TREE_DATA_PROVIDER } from './ou-tree-data-provider.token';

@Component({
  selector: 'cdw-ou-tree-picker-matcher',
  standalone: true,
  imports: [CdwFilteredTreePickerModule, CommonUiComponentsSpinnerComponent, ReactiveFormsModule],
  template: `
    @if (loading) {
      <div style="display: flex; justify-content: center; align-items: center; height: 300px;">
        <lib-cdw-spinner size="medium"></lib-cdw-spinner>
      </div>
    } @else {
      <div [formGroup]="state">
        <lib-cdw-filtered-tree-picker
          formControlName="ouSelection"
          [dataSource]="ouTreeData"
          [multiSelect]="true"
          [progressiveParentSelect]="true"
          height="300px"
          filterLabel="Search OUs"
          filterPlaceholder="Type to filter..."
        ></lib-cdw-filtered-tree-picker>
      </div>
    }
  `,
})
export class OuTreePickerMatcherComponent
  extends BaseMatcherEditorComponent
  implements OnInit
{
  loading = true;
  ouTreeData: TreeNode[] = [];

  public override state = new FormGroup({
    ouSelection: new FormControl<string[]>([]),
  });

  constructor(
    @Inject(OU_TREE_DATA_PROVIDER) private ouTreeDataProvider: IOrgUnitsDataService,
  ) {
    super();
    this.matcherDisplayName = OuTreePickerMatcher.displayName;
    this.matcherName = OuTreePickerMatcher.name;

    this.state.valueChanges.subscribe((formState) => {
      const selected = formState.ouSelection ?? [];
      if (!selected.length) {
        this.valueToMatch = null;
        return;
      }

      const collapsed = OuTreePickerMatcherComponent.collapseSubtrees(selected, this.ouTreeData);
      if (!collapsed) {
        // All OUs selected — emit empty arrays so backend skips the WHERE clause entirely
        this.valueToMatch = JSON.stringify({ exact: [], subtrees: [], totalCount: selected.length });
        return;
      }

      this.valueToMatch = JSON.stringify({ exact: collapsed.exact, subtrees: collapsed.subtrees, totalCount: collapsed.totalCount });
    });
  }

  ngOnInit(): void {
    this.ouTreeDataProvider.getOrgUnits().subscribe((treeData) => {
      this.ouTreeData = treeData;
      this.restoreSelection(treeData);
      this.loading = false;
    });

    this.hasErrors$ = this.state.valueChanges.pipe(
      map((formState) => !(formState.ouSelection?.length))
    );
  }

  private restoreSelection(treeData: TreeNode[]): void {
    if (!this.matcherOptions?.matchValue) return;

    try {
      const parsed = JSON.parse(this.matcherOptions.matchValue as string);

      if (Array.isArray(parsed)) {
        this.state.get('ouSelection')?.setValue(parsed);
      } else {
        const exact: string[] = parsed.exact ?? [];
        const subtrees: string[] = parsed.subtrees ?? [];

        // Both empty + totalCount > 0 means "all OUs selected"
        if (!(exact.length || subtrees.length ) && parsed.totalCount > 0) {
          const allPaths = treeData.flatMap((root) => OuTreePickerMatcherComponent.getAllValues(root));
          this.state.get('ouSelection')?.setValue(allPaths);
        } else if (exact.length || subtrees.length) {
          const expandedPaths = [
            ...exact,
            ...OuTreePickerMatcherComponent.expandSubtreePaths(subtrees, treeData),
          ];
          this.state.get('ouSelection')?.setValue(expandedPaths);
        }
      }
    } catch {
      this.state.get('ouSelection')?.setValue([this.matcherOptions.matchValue as string]);
    }
  }

  /**
   * Collapse fully-selected subtrees into parent-only representations.
   *
   * Given all selected paths and the full OU tree, detects when a parent node
   * and ALL its descendants are selected. In that case, only the parent path
   * is kept (as a "subtree" entry) and all descendants are removed.
   *
   * Returns null if selectedPaths/treeData is empty OR if ALL OUs are selected.
   */
  static collapseSubtrees(
    selectedPaths: string[],
    treeData: TreeNode[]
  ): CollapsedOuSelection | null {
    if (!(selectedPaths.length && treeData.length)) return null;

    const selectedSet = new Set(selectedPaths);
    const subtrees: string[] = [];
    const coveredBySubtree = new Set<string>();

    OuTreePickerMatcherComponent.findCollapsibleSubtrees(treeData, selectedSet, subtrees, coveredBySubtree);

    const subtreeSet = new Set(subtrees);
    const exact = selectedPaths.filter(
      (p) => !(coveredBySubtree.has(p) || subtreeSet.has(p))
    );

    // All top-level nodes are fully-selected subtrees → everything selected
    if (exact.length === 0 && treeData.every((root) => subtreeSet.has(root.value as string))) {
      return null;
    }

    return { exact, subtrees, totalCount: selectedPaths.length };
  }

  /**
   * Expand subtree paths back to individual paths using the tree structure.
   * Used when restoring a collapsed selection for tree display.
   */
  static expandSubtreePaths(subtreePaths: string[], treeData: TreeNode[]): string[] {
    const result: string[] = [];

    for (const path of subtreePaths) {
      const node = OuTreePickerMatcherComponent.findNode(path, treeData);
      if (node) {
        result.push(...OuTreePickerMatcherComponent.getAllValues(node));
      } else {
        result.push(path);
      }
    }

    return result;
  }

  private static isSubtreeFullySelected(node: TreeNode, selectedSet: Set<string>): boolean {
    if (!selectedSet.has(node.value as string)) return false;
    if (!node.children?.length) return true;
    return node.children.every((child) => OuTreePickerMatcherComponent.isSubtreeFullySelected(child, selectedSet));
  }

  private static findCollapsibleSubtrees(
    nodes: TreeNode[],
    selectedSet: Set<string>,
    subtrees: string[],
    coveredBySubtree: Set<string>,
  ): void {
    for (const node of nodes) {
      const nodeValue = node.value as string;

      if (!selectedSet.has(nodeValue)) {
        if (node.children) OuTreePickerMatcherComponent.findCollapsibleSubtrees(node.children, selectedSet, subtrees, coveredBySubtree);
        continue;
      }

      if (node.children?.length && OuTreePickerMatcherComponent.isSubtreeFullySelected(node, selectedSet)) {
        subtrees.push(nodeValue);
        for (const desc of OuTreePickerMatcherComponent.getAllDescendantValues(node)) {
          coveredBySubtree.add(desc);
        }
      } else {
        if (node.children) OuTreePickerMatcherComponent.findCollapsibleSubtrees(node.children, selectedSet, subtrees, coveredBySubtree);
      }
    }
  }

  private static findNode(value: string, nodes: TreeNode[]): TreeNode | undefined {
    for (const node of nodes) {
      if (node.value === value) return node;
      if (node.children) {
        const found = OuTreePickerMatcherComponent.findNode(value, node.children);
        if (found) return found;
      }
    }
    return undefined;
  }

  private static getAllValues(node: TreeNode): string[] {
    const values = [node.value as string];
    for (const child of node.children ?? []) {
      values.push(...OuTreePickerMatcherComponent.getAllValues(child));
    }
    return values;
  }

  private static getAllDescendantValues(node: TreeNode): string[] {
    const result: string[] = [];
    for (const child of node.children ?? []) {
      result.push(child.value as string);
      result.push(...OuTreePickerMatcherComponent.getAllDescendantValues(child));
    }
    return result;
  }
}
